import { Injectable } from '@angular/core';
import { Site } from '@app/models/site.model';
import { Capacitor } from '@capacitor/core';
import { environment } from '@env/environment';
import { Base64 } from 'js-base64';
import { DeviceDetectorService } from 'ngx-device-detector';

const piexif = require('src/assets/scripts/piexif.js');

@Injectable({
  providedIn: 'root'
})

export class UtilsService {
  constructor(private _deviceService: DeviceDetectorService) {
  }

  // Mobile app and mobile browser
  isMobileDevice() {
    return this._deviceService.isMobile() || this._deviceService.isTablet();
  } 

  // Mobile app only
  isMobileAndTablet(){
    return Capacitor.isNativePlatform();
  } 

  // Web only
  isWebBrowser(){
    return !Capacitor.isNativePlatform();
  }

  isUndefinedOrNullOrEmpty(value: string): boolean {
    return value == undefined || value == null || value.trim().length <= 0;
  }

  isUndefinedOrNull<T>(value: T | null | undefined): boolean {
    return value == undefined || value == null;
  }

  isAndroid(): boolean {
    const deviceOS = this._deviceService.getDeviceInfo().os.toLowerCase();
    return deviceOS.toLowerCase() == 'android';
  }

  isIOS(): boolean {
    const deviceOS = this._deviceService.getDeviceInfo().os.toLowerCase();
    return deviceOS.toLowerCase() == 'ios' || deviceOS.toLowerCase() == 'mac';
  }

  isEdgeBrowser(){
    return (this._deviceService.browser().toLowerCase() == "ms-edge-chromium");
  }

  getDistanceBetweenTwoPointsinKM(lat1: number, long1: number, lat2: number, long2: number) {
    if (lat1 == lat2 && long1 == long2)
      return 0;
  
    const radlat1 = (Math.PI * lat1) / 180;
    const radlat2 = (Math.PI * lat2) / 180;
  
    const theta = long1 - long2;
    const radtheta = (Math.PI * theta) / 180;
  
    let dist =
      Math.sin(radlat1) * Math.sin(radlat2) +
      Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
  
    if (dist > 1) {
      dist = 1;
    }
  
    dist = Math.acos(dist);
    dist = (dist * 180) / Math.PI;
    dist = dist * 60 * 1.1515;
    dist = dist * 1.609344; //convert miles to km
    
    return dist;
  }

  checkMimeType(file: File): string | undefined {
    let extension = file.name.substring(file.name.lastIndexOf('.')+1);
    if (extension.toLowerCase() === 'jpeg') extension = 'jpg';
    let fileType = undefined;
    if (file.type && file.type.length > 0) {
      switch (file.type) {
        case "text/plain":
          fileType = "txt";
          break;
        case "application/msword":
          fileType = "doc";
          break;
        case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
          fileType = "docx";
          break;
        case "application/pdf":
          fileType = "pdf";
          break;
        case "image/jpeg":
          fileType = "jpg";
          break;
        case "image/png":
          fileType = "png";
          break;
        default:
          fileType = undefined;
      }
      if (!(fileType && extension && fileType.toLowerCase() === extension.toLowerCase()))
        return undefined;
      return fileType;
    } 
    else return undefined;
  }

  getBase64StringFileExtension(dataUrl: string | undefined): string {
    let result = '';
    let fileType = dataUrl?.replace(/^.+,/, '').substring(0, 5).toUpperCase();
    if (fileType && fileType.length > 0) {
      switch (fileType) {
        case "IVBOR":
          return "png";
        case "/9J/4":
          return "jpg";
        /* for possible later usage
        case "AAAAF":
          return "mp4";
        case "JVBER":
          return "pdf";
        case "AAABA":
          return "ico";
        case "UMFYI":
          return "rar";
        case "E1XYD":
          return "rtf";
        case "U1PKC":
          return "txt";
        case "MQOWM":
        case "77U/M":
          return "srt";
        */
        default:
          return result;
      }
    } else return result;    
  }

  removeExifMetadataFromDataUrl(dataUrl: string): string {
    let part1 = dataUrl.split(",")[0];
    let part2 = dataUrl.split(",")[1];
    let decodedData = atob(part2);
    let oldExifObj = piexif.load(decodedData);
    var zeroth: Record<number, number> = {};
    let orientation = oldExifObj['0th'][piexif.ImageIFD.Orientation];
    zeroth[piexif.ImageIFD.Orientation] = orientation;
    var newExifObj = {"0th":zeroth};
    var newExifStr = piexif.dump(newExifObj);
    piexif.remove(decodedData);
    let updatedData = piexif.insert(newExifStr, decodedData);
    let encodedData = btoa(updatedData);
    return `${part1},${encodedData}`;
  }

  updateFileFormatToAcceptedFormat(...args: string[]): string {
    let finalFormat = '';
    let firstLoop = true;
    args?.forEach(data => {
      if(data){
        let val = data.split(',');
        val.forEach(i => {
          if(i){
            if(firstLoop){
              finalFormat = "." + i;
              firstLoop = false;
            }else{
              finalFormat = finalFormat + ",." + i;
            }
          }
        })
      }
    });
    return finalFormat;
  }

  isNotAndroidOrIOS(): boolean {
    const deviceOS = this._deviceService.getDeviceInfo().os.toLowerCase();
    return deviceOS.toLowerCase() !== 'android' && deviceOS !== 'ios';
  }

  convertKilometersToMeters(kilometers: number): number {
    return kilometers * 1000;
  }

  encodeBase64String(data: string): string {
    return Base64.encode(data); 
  }

  decodeBase64String(data: string): string {
    return Base64.decode(data); 
  }

  generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r: number = Math.random() * 16 | 0;
      const v: number = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  isGuid(value: string): boolean {
    if (typeof value !== 'string' || !value){
      return false;
    }

    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return guidRegex.test(value);
  }

   public checkSiteIsInRange(site : Site, distanceInMeters: number): boolean {
    let result: boolean = false;
    if (site) {
      const rangeToValidate = site.geofencingRangeMeter > 0 ? site.geofencingRangeMeter : environment.geofencingRangeMeterDefault;
      if (site.isGeofencingEnable) {
        if (distanceInMeters <= rangeToValidate) {
          result = true;
        }
      }
      else {
        result = true;
      }
    }
    
    return result;
  }

  createThumbnail(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      const MAX_WIDTH = 150;
      const MAX_HEIGHT = 150;
      const JPEG_QUALITY = 0.6;

      reader.onload = () => {
        img.src = reader.result as string;
      };

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject('Could not create canvas context');
          return;
        }

        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height = Math.round(height * (MAX_WIDTH / width));
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width = Math.round(width * (MAX_HEIGHT / height));
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(img, 0, 0, width, height);

        const thumbnailBase64 = canvas
          .toDataURL('image/jpeg', JPEG_QUALITY)
          .split(',')[1];

        resolve(thumbnailBase64);
      };

      reader.onerror = reject;
      img.onerror = reject;

      reader.readAsDataURL(file);
    });
  }
}